import { ButtonLink, Card, Eyebrow, Heading, Lead, Section } from '../components/ui';
import { usePageTitle } from '../lib/usePageTitle';

/**
 * How the agent works, for a reader deciding whether to trust it.
 *
 * Written from the implementation in `apps/wave-agent`, not from a roadmap.
 * That is the constraint worth preserving if this page is ever extended: every
 * claim here names something that exists in the repository - the router, the
 * trace collector, the miner, the sanitizer and dataset gates, the NumPy
 * trainer, the held-out benchmark, shadow mode, the promotion gates. The claim
 * it deliberately does *not* make is that a trained local model ships switched
 * on, and the one it refuses to invent is a savings percentage, because nothing
 * in the codebase measures one.
 *
 * This lived under the founder card on the About page until it earned its own
 * route. Nothing about the copy assumes either home, so it can move again.
 */

/**
 * The self-learning loop, in the order the code runs it.
 *
 * Sourced from the agent implementation in `apps/wave-agent`, not from a
 * roadmap: each step below names something that exists - the trace collector,
 * the miner, the sanitizer and dataset gates, the trainer, the held-out
 * benchmark, shadow mode, and the promotion gates. The claim this page is
 * careful *not* to make is that a trained local model ships switched on; a
 * local answer begins only once a model has been trained and has survived
 * shadow, which is what the last two steps say.
 */
const LEARNING_LOOP: { title: string; body: string }[] = [
  {
    title: 'Collect',
    body:
      'Each run appends traces as it goes rather than at the end, so a ' +
      'verification that fails on its fortieth criterion still keeps the ' +
      'thirty-nine it earned. Recording a trace can never fail a run: if the ' +
      'disk is full, the run reports as normal and the trace is simply not kept.',
  },
  {
    title: 'Mine',
    body:
      'Most traces teach a model nothing it does not already do. The valuable ' +
      'ones are where something went wrong informatively - the local model and ' +
      'the remote one disagreed, a confident answer had to be corrected by a ' +
      'person, a check passed that should not have, or the page was unlike ' +
      'anything seen before. A hard example is not a low-quality example, and ' +
      'they are ranked by what the error would have cost rather than by how ' +
      'often it happens.',
  },
  {
    title: 'Sanitise and gate',
    body:
      'Training data reaches the corpus only through a sanitizer and a set of ' +
      'trust gates. The trainer has no other input, so training on unvalidated ' +
      'data is not a policy that can be forgotten - there is no code path for it.',
  },
  {
    title: 'Train',
    body:
      'Real gradient descent on the collected corpus: forward, loss, backward, ' +
      'optimiser step, with a learning-rate schedule, gradient accumulation, ' +
      'early stopping and resumable checkpoints. The validation split is held ' +
      'out before the first step, because a model selected on data it trained ' +
      'on has a quality score that is fiction.',
  },
  {
    title: 'Benchmark',
    body:
      'Whole projects - not just examples - are held out for the benchmark, so ' +
      'the score measures generalisation to an application the model has never ' +
      'seen. A metric that could not be measured counts as a failure, never as ' +
      'a pass, so a broken evaluation harness cannot quietly approve everything.',
  },
  {
    title: 'Shadow',
    body:
      'A candidate answers the same questions as the trusted path, in parallel. ' +
      'Its answers are recorded and discarded; production is unaffected. This is ' +
      'how a model earns trust without ever having been trusted, which is the ' +
      'only safe order - the alternative is learning it was wrong from a ' +
      'customer report. Every disagreement is kept as a training example.',
  },
  {
    title: 'Promote, or roll back',
    body:
      'Before it may serve, a candidate must pass every gate: artefact ' +
      'verification, structured output, evidence grounding, prompt-injection ' +
      'resistance, secret handling, the benchmark comparison, and resource ' +
      'limits. There is no weighted score, because a model that leaks secrets ' +
      'must not be promoted on the strength of its accuracy. A regression in ' +
      'production sends it back.',
  },
];

/** Where remote spend actually falls, each tied to a mechanism in the agent. */
const COST_LEVERS: { title: string; body: string }[] = [
  {
    title: 'Deterministic checks cost nothing',
    body:
      'Anything that can be settled by executing code is settled by executing ' +
      'code, and no model is consulted at all. This is the largest lever and ' +
      'the least glamorous one.',
  },
  {
    title: 'The agent stops rediscovering your application',
    body:
      'Without memory, every run works out again that the sign-in field is ' +
      '#email and that the invoice criterion is only reachable from /billing - ' +
      'each rediscovery costing a model call, and each able to come out ' +
      'differently on two runs of unchanged software. What the agent learns ' +
      'about a project persists between runs.',
  },
  {
    title: 'Retrieval sends the passage, not the document',
    body:
      'Project documents are chunked and indexed, and the run sends the ' +
      'relevant passages rather than the whole specification. The index keeps ' +
      'itself in step by content hash - you edit the file, the next run ' +
      'notices, and nobody runs an indexer.',
  },
  {
    title: 'Local answers displace remote ones as trust is earned',
    body:
      'Routing thresholds are per task, because field mapping tolerates a ' +
      'mistake that evaluating an acceptance criterion does not. They move only ' +
      'on measured accuracy, in bounded steps, and never below a floor - so the ' +
      'share of work handled locally grows with evidence rather than optimism.',
  },
];

export function Architecture() {
  usePageTitle('Architecture — How the WaveQA Agent Works');

  return (
    <>
      <Section className="border-b border-hull-800">
        <Eyebrow>Architecture</Eyebrow>
        <Heading level={1} className="text-white">
          How the WaveQA Agent Works
        </Heading>
        <Lead className="mt-5 max-w-3xl">
          WaveQA is built around a single execution rule, and most of the other
          design decisions follow from it:
        </Lead>

        <blockquote className="mt-6 max-w-3xl border-l-2 border-surge-500 pl-5">
          <p className="text-lg font-medium leading-relaxed text-white sm:text-xl">
            Deterministic where possible, local where reliable, remote where
            necessary &mdash; with evidence controlling the result at every step.
          </p>
        </blockquote>

        <p className="mt-6 max-w-3xl text-base leading-relaxed text-hull-300">
          That order is not a cost preference. It is a ranking by
          trustworthiness. A deterministic check is reproducible and free. A
          local answer is cheap and private, but only as good as its last
          benchmark. A remote model is the expensive option and the only one
          that can reason about a case nothing has seen before. Reaching for
          them in that order is what lets the system get cheaper as it gets
          better, rather than the reverse.
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          <Card as="article">
            <h3 className="text-lg font-semibold text-white">
              Evidence decides. The model proposes.
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-hull-300">
              A model never authorises a passing result. It returns a proposal;
              only captured evidence can turn an acceptance criterion green.
              Model output is treated as untrusted input and has exactly one
              route into anything the agent acts on &mdash; a validation gate
              that cannot be bypassed, because the validated type refuses to be
              constructed anywhere else. High confidence is not accepted as a
              substitute for a gate that passed.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-hull-300">
              A task may always escalate to a stronger model. Nothing can move a
              high-risk task <em>down</em> to a cheaper one on the strength of a
              number the model reported about itself.
            </p>
          </Card>

          <Card as="article">
            <h3 className="text-lg font-semibold text-white">
              A task is a contract, not a prompt
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-hull-300">
              Every operation a model may be asked to perform is declared up
              front, with its output schema, risk level, evidence requirement and
              confidence policy. A task type that has not been declared cannot be
              dispatched at all.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-hull-300">
              The pipeline states what it needs decided and never who decides it.
              That is what allows the answerer to change &mdash; deterministic,
              local, or remote &mdash; without anything above that boundary
              changing with it, and it is why the local model can be introduced
              without rewriting the agent around it.
            </p>
          </Card>
        </div>

        <Heading level={3} className="mt-14 text-white">
          The self-learning loop
        </Heading>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-hull-300">
          Every run leaves a trace of what was asked, what was answered and what
          the evidence showed. Those traces are what a local model is trained on,
          and the path from one to the other is deliberately narrow:
        </p>

        <ol className="mt-6 max-w-3xl space-y-4">
          {LEARNING_LOOP.map((step, index) => (
            <li key={step.title} className="flex gap-4">
              <span
                aria-hidden="true"
                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-hull-600 bg-hull-900/60 text-xs font-semibold text-surge-300"
              >
                {index + 1}
              </span>
              <div>
                <h4 className="text-sm font-semibold text-white">{step.title}</h4>
                <p className="mt-1 text-sm leading-relaxed text-hull-300">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <p className="mt-8 max-w-3xl text-base leading-relaxed text-hull-300">
          There is no shortcut from candidate to production. A model reaches live
          traffic through shadow mode or not at all, and two independent guards
          enforce that rather than one &mdash; neither sufficient on its own.
        </p>

        <Heading level={3} className="mt-14 text-white">
          The WaveQA-LLM, built rather than borrowed
        </Heading>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-hull-300">
          The local model is a decoder-only transformer &mdash; pre-norm blocks,
          causal multi-head attention, a tied output head &mdash; implemented
          directly against NumPy, with the gradients derived by hand. That is a
          choice rather than a limitation. Installing a QA tool should not mean
          installing a two-gigabyte deep-learning stack to run a model measured
          in tens of megabytes.
        </p>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-hull-300">
          Deriving the gradients by hand also makes them checkable: every
          parameter&rsquo;s analytic gradient is compared against a numerical one
          in the test suite. A single wrong term trains a model that looks like
          it is learning and is not &mdash; a failure that is silent underneath
          an autograd engine and loud underneath a finite-difference check.
        </p>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-hull-300">
          The result is small enough to sit beside a browser on the machine
          already running the tests, which is the whole point: the cheapest and
          most private model call is the one that never leaves the laptop.
        </p>

        <Heading level={3} className="mt-14 text-white">
          Where the cost actually goes down
        </Heading>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-hull-300">
          Remote inference is billed by the token, so the saving does not come
          from finding a cheaper provider. It comes from asking a remote model
          fewer questions:
        </p>
        <ul className="mt-6 max-w-3xl space-y-4">
          {COST_LEVERS.map((lever) => (
            <li key={lever.title} className="border-l-2 border-hull-700 pl-4">
              <h4 className="text-sm font-semibold text-white">{lever.title}</h4>
              <p className="mt-1 text-sm leading-relaxed text-hull-300">{lever.body}</p>
            </li>
          ))}
        </ul>
        <p className="mt-8 max-w-3xl text-base leading-relaxed text-hull-300">
          We publish the mechanism rather than a percentage. What a given team
          saves depends on how repetitive their suite is and how far their local
          model has been trained, and a headline number that ignored both would
          be marketing rather than measurement.
        </p>

        <Heading level={3} className="mt-14 text-white">
          What stays on your machine
        </Heading>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-hull-300">
          Traces are written to your own disk, and nothing in the collector
          transmits. Using one organisation&rsquo;s runs to improve a model that
          serves anyone else is a separate, explicitly authorised decision
          &mdash; a collector that quietly uploaded would be taking that decision
          on everyone&rsquo;s behalf.
        </p>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-hull-300">
          Retrieval is limited to a closed list of approved sources: your own
          project documents, your compliance configuration, and run summaries
          that already passed validation. Live web content and the tested
          application&rsquo;s own output are never indexed &mdash; an agent that
          learns from the system it is testing is an agent that can be taught to
          approve it.
        </p>
      </Section>

      <Section>
        <Card className="text-center">
          <Heading className="text-white">Questions about the internals?</Heading>
          <Lead className="mx-auto mt-3 max-w-xl">
            Security review, deployment topology, or how the agent would fit
            your pipeline — we would rather answer than have you guess.
          </Lead>
          <div className="mt-7">
            <ButtonLink to="/contact">Get in touch</ButtonLink>
          </div>
        </Card>
      </Section>
    </>
  );
}
